import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Button } from '@sentinel/ui';
import type { ConsentInfo } from '../types';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const styles = stylex.create({
  card: { width: '100%', display: 'grid', gap: '1.25rem' },
  header: { display: 'grid', gap: '0.375rem' },
  title: {
    margin: 0,
    fontFamily:
      sentinel.fontDisplay,
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
  },
  subtitle: { margin: 0, color: sentinel.textMuted, lineHeight: 1.5 },
  appRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    alignItems: 'center',
    gap: '0.875rem',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    paddingLeft: '0.875rem',
    paddingRight: '0.875rem',
    borderRadius: '0.625rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
  },
  icon: {
    width: '3rem',
    height: '3rem',
    borderRadius: '0.75rem',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: sentinel.brandPrimary,
    color: sentinel.textOnAccent,
    fontWeight: 700,
    fontSize: '1.25rem',
  },
  appName: { fontWeight: 600, fontSize: '1.0625rem' },
  appText: { display: 'grid', gap: '0.125rem', minWidth: 0 },
  appHost: { fontSize: '0.8125rem', color: sentinel.textMuted, overflowWrap: 'anywhere' },
  willCaption: { margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: sentinel.textMuted },
  scopeList: { margin: 0, paddingLeft: 0, display: 'grid', gap: '0.5rem', listStyle: 'none' },
  scopeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    paddingTop: '0.625rem',
    paddingBottom: '0.625rem',
    paddingLeft: '0.875rem',
    paddingRight: '0.875rem',
    borderRadius: '0.5rem',
    backgroundColor: sentinel.surfaceOverlay,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    fontSize: '0.9375rem',
    fontWeight: 500,
  },
  badge: {
    fontSize: '0.6875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    borderRadius: '999px',
    marginLeft: 'auto',
    fontWeight: 600,
  },
  badgeNew: {
    // brandPrimary — preserve tenant override via CSS var, fallback to VicOne pink.
    backgroundColor: `var(--sentinel-brand-primary, ${sentinel.brandPrimary})`,
    color: sentinel.textOnAccent,
  },
  badgeGranted: {
    // riskSafe (darkened 50% L — see commit 5727b62e6) on textOnAccent white.
    backgroundColor: sentinel.riskSafe,
    color: sentinel.textOnAccent,
  },
  buttonRow: { display: 'flex', gap: '0.75rem', marginTop: '0.25rem' },
  signedIn: {
    margin: 0,
    fontSize: '0.75rem',
    color: sentinel.textMuted,
    textAlign: 'center',
  },
});

export interface ConsentCardProps {
  info: ConsentInfo;
  userEmail: string | null;
  submitting: 'allow' | 'deny' | null;
  onAllow: () => void;
  onDeny: () => void;
}

export function ConsentCard({ info, userEmail, submitting, onAllow, onDeny }: ConsentCardProps) {
  const { t } = useTranslation('consent');
  const firstLetter = (info.client_name.charAt(0) || '?').toUpperCase();
  const grantedSet = new Set(info.existing_scopes);
  const ordered = [
    ...info.existing_scopes.map((s) => ({ name: s, granted: true })),
    ...info.requested_scopes
      .filter((s) => !grantedSet.has(s))
      .map((s) => ({ name: s, granted: false })),
  ];

  return (
    <section {...stylex.props(styles.card)} data-testid="consent-card">
      <header {...stylex.props(styles.header)}>
        <h1 {...stylex.props(styles.title)}>{t('title', { client: info.client_name })}</h1>
        <p {...stylex.props(styles.subtitle)}>{t('subtitle', { client: info.client_name })}</p>
      </header>

      <div {...stylex.props(styles.appRow)}>
        <div {...stylex.props(styles.icon)} aria-hidden="true">
          {firstLetter}
        </div>
        <div {...stylex.props(styles.appText)}>
          <div {...stylex.props(styles.appName)}>{info.client_name}</div>
          {info.client_host ? (
            <div {...stylex.props(styles.appHost)} data-testid="consent-client-host">
              {t('client_host_label', { host: info.client_host })}
            </div>
          ) : null}
        </div>
      </div>

      <div>
        <p {...stylex.props(styles.willCaption)}>{t('will_be_able_to')}</p>
        <ul {...stylex.props(styles.scopeList)} data-testid="consent-scope-list">
          {ordered.map(({ name, granted }) => (
            <li key={name} {...stylex.props(styles.scopeItem)}>
              <span>{name}</span>
              <span {...stylex.props(styles.badge, granted ? styles.badgeGranted : styles.badgeNew)}>
                {granted ? t('scope_already_granted') : t('scope_new')}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div {...stylex.props(styles.buttonRow)}>
        <Button
          type="button"
          variant="secondary"
          onClick={onDeny}
          disabled={submitting !== null}
          data-testid="consent-deny"
        >
          {t('deny')}
        </Button>
        <Button
          type="button"
          onClick={onAllow}
          disabled={submitting !== null}
          data-testid="consent-allow"
        >
          {t('allow')}
        </Button>
      </div>

      {userEmail ? (
        <p {...stylex.props(styles.signedIn)}>
          {t('signed_in_as')} <strong>{userEmail}</strong>
        </p>
      ) : null}
    </section>
  );
}
